/** Formatação e agregação de reembolsos (labels + cards com filtros). */

export const PAYMENT_TYPE_LABELS = {
  cartao_empresarial: 'Cartão empresarial',
  dinheiro_proprio: 'Dinheiro próprio',
};

export function formatPaymentType(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  const key = s.toLowerCase();
  if (PAYMENT_TYPE_LABELS[key]) return PAYMENT_TYPE_LABELS[key];
  const known = Object.values(PAYMENT_TYPE_LABELS).find((l) => l.toLowerCase() === key);
  if (known) return known;
  return s;
}

export function formatBrl(n) {
  return Number(n || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function sumAmount(list, pred) {
  return list.reduce((t, e) => t + (pred == null || pred(e) ? Number(e.amount) || 0 : 0), 0);
}

function buildStatSegments(expenses) {
  const total = sumAmount(expenses) || 1;
  return ['pendente', 'aprovado', 'pago', 'revisado', 'rejeitado', 'negado'].map((st) => {
    const amt = sumAmount(expenses, (i) => i.statusKey === st);
    return {
      statusKey: st,
      widthPct: Math.round((amt / total) * 1000) / 10,
    };
  });
}

function todayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatPeriodDay(iso, { withYear = true } = {}) {
  if (!iso) return '';
  try {
    const opts = withYear
      ? { day: '2-digit', month: 'short', year: 'numeric' }
      : { day: '2-digit', month: 'short' };
    return new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', opts).replace(/\./g, '');
  } catch {
    return String(iso);
  }
}

function yearOfIso(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const m = iso.match(/^(\d{4})/);
  return m ? m[1] : '';
}

export function formatPeriodRangeLabel(start, end) {
  const yA = yearOfIso(start);
  const yB = yearOfIso(end);
  const sameYear = yA && yB && yA === yB;
  /* mesmo ano: "27 de jul – 02 de ago de 2026"; anos distintos com year em ambos */
  const a = formatPeriodDay(start, { withYear: !sameYear });
  const b = formatPeriodDay(end, { withYear: true });
  if (a && b && a !== b) return `${a} – ${b}`;
  if (a || b) return a || b;
  return 'Período —';
}

function expensePeriodBounds(e) {
  const start = e.periodStart || e.expenseDate || '';
  const end = e.periodEnd || e.periodStart || e.expenseDate || '';
  return { start, end };
}

function isOpenStatus(statusKey) {
  return statusKey === 'pendente' || statusKey === 'revisado';
}

function hasOpenItems(list) {
  return (list || []).some((e) => isOpenStatus(e.statusKey));
}

/**
 * Agrupa lançamentos por (period_start, period_end).
 * Ordenado do mais recente ao mais antigo (por fim, depois início).
 */
export function groupExpensesByPeriod(expenses) {
  const map = new Map();
  for (const e of expenses || []) {
    const { start, end } = expensePeriodBounds(e);
    if (!start && !end) continue;
    const key = `${start}|${end}`;
    if (!map.has(key)) {
      map.set(key, { periodStart: start || null, periodEnd: end || null, expenses: [] });
    }
    map.get(key).expenses.push(e);
  }
  return [...map.values()].sort((a, b) => {
    const byEnd = String(b.periodEnd || '').localeCompare(String(a.periodEnd || ''));
    if (byEnd) return byEnd;
    return String(b.periodStart || '').localeCompare(String(a.periodStart || ''));
  });
}

export function periodKeyOf(periodStart, periodEnd) {
  return `${periodStart || ''}|${periodEnd || ''}`;
}

function isPeriodFuture(periodStart, periodEnd, today = todayIsoLocal()) {
  const s = periodStart || periodEnd || '';
  if (!s) return false;
  return today < s;
}

/**
 * Períodos atuais ou passados do técnico (dropdown do detalhe).
 * Ordenados por period_end mais recente primeiro.
 */
export function listTechSelectablePeriods(expenses, today = todayIsoLocal()) {
  return groupExpensesByPeriod(expenses)
    .filter((g) => !isPeriodFuture(g.periodStart, g.periodEnd, today))
    .map((g) => {
      const key = periodKeyOf(g.periodStart, g.periodEnd);
      const label = formatPeriodRangeLabel(g.periodStart, g.periodEnd);
      return {
        key,
        periodStart: g.periodStart,
        periodEnd: g.periodEnd,
        label,
        expenses: g.expenses,
        isActive: isPeriodActive(g.periodStart, g.periodEnd, today),
      };
    });
}

export function expenseMatchesPeriodKey(expense, periodKey) {
  if (!periodKey) return true;
  const { start, end } = expensePeriodBounds(expense);
  return periodKeyOf(start, end) === periodKey;
}

export function isPeriodActive(periodStart, periodEnd, today = todayIsoLocal()) {
  const s = periodStart || periodEnd || '';
  const e = periodEnd || periodStart || '';
  if (!s || !e) return false;
  return today >= s && today <= e;
}

export function isPeriodPast(periodEnd, today = todayIsoLocal()) {
  const e = periodEnd || '';
  if (!e) return false;
  return today > e;
}

/**
 * Resolve o que o card exibe:
 * - métricas: período vigente (hoje dentro de – até), senão o último registrado
 * - se o último já passou com pendência: dados normais + aviso compacto (sem detalhar aqui)
 * - se vigente: sempre exibe faixa de – até
 */
export function resolveTechPeriodView(expenses) {
  const groups = groupExpensesByPeriod(expenses);
  if (!groups.length) return null;

  const today = todayIsoLocal();
  const last = groups[0];
  const active = groups.find((g) => isPeriodActive(g.periodStart, g.periodEnd, today));
  const lastIsPast = isPeriodPast(last.periodEnd || last.periodStart, today);
  const lastHasOpen = hasOpenItems(last.expenses);
  const pastWithOpen = groups.filter(
    (g) => isPeriodPast(g.periodEnd || g.periodStart, today) && hasOpenItems(g.expenses)
  );

  let isHistory = false;
  // Visível: período vigente, passado com pendência, ou histórico (último fechado)
  if (active) {
    // sempre mostra se há período aberto (dados do período atual)
  } else if (lastIsPast && lastHasOpen) {
    // último passou mas com pendência → exibe com aviso compacto
  } else if (pastWithOpen.length) {
    // há período passado com pendência (pode ser anterior ao last se last sem open)
  } else if (last) {
    isHistory = true;
  } else {
    return null;
  }

  const display = active || last;
  const displayList = display.expenses;
  const periodIsActive = isPeriodActive(display.periodStart, display.periodEnd, today);
  const periodLabel = formatPeriodRangeLabel(display.periodStart, display.periodEnd);

  const pastOpenCount = isHistory ? 0 : pastWithOpen.length;
  const showPastOpenHint = pastOpenCount > 0;
  const pastOpenPeriods = pastWithOpen.map((g) => ({
    periodStart: g.periodStart,
    periodEnd: g.periodEnd,
    label: formatPeriodRangeLabel(g.periodStart, g.periodEnd),
  }));

  return {
    display,
    displayList,
    periodStart: display.periodStart,
    periodEnd: display.periodEnd,
    periodLabel,
    periodIsActive,
    showPastOpenHint,
    pastOpenCount,
    pastOpenPeriods: isHistory ? [] : pastOpenPeriods,
    isHistory,
    lastPeriod: last,
  };
}

/**
 * Reconstroi o card do overview a partir dos lançamentos do técnico.
 * @returns {object|null}
 */
export function rebuildTechCardFromExpenses(baseTech, expenses) {
  const view = resolveTechPeriodView(expenses);
  if (!view) return null;

  const list = view.displayList;
  const total = sumAmount(list);
  const pend = list.filter((i) => i.statusKey === 'pendente');
  const revis = list.filter((i) => i.statusKey === 'revisado');
  const byStatus = (st) => sumAmount(list, (i) => i.statusKey === st);

  // alertas de meta só do período exibido (dados normais)
  const alerts = [];
  if (pend.length) alerts.push(`${pend.length} pend.`);
  if (revis.length) alerts.push(`${revis.length} revis.`);

  return {
    ...baseTech,
    total,
    totalLabel: formatBrl(total),
    count: list.length,
    countLabel: `${list.length} lanç.`,
    pendCount: pend.length,
    revisCount: revis.length,
    pendAlert: alerts.join(' · '),
    // no overview: open só do período vigente (sem “explodir” pendência do passado)
    hasOpenPending: view.periodIsActive && pend.length > 0,
    hasOpenRevised: view.periodIsActive && revis.length > 0,
    periodStart: view.periodStart,
    periodEnd: view.periodEnd,
    periodLabel: view.periodLabel,
    periodIsActive: view.periodIsActive,
    showPastOpenHint: view.showPastOpenHint,
    pastOpenCount: view.pastOpenCount || 0,
    pastOpenPeriods: view.pastOpenPeriods || [],
    isHistory: Boolean(view.isHistory),
    lastLabel: view.periodLabel,
    segments: buildStatSegments(list),
    stats: [
      { statusKey: 'pendente', label: 'Pendente', value: formatBrl(byStatus('pendente')), amount: byStatus('pendente') },
      { statusKey: 'pago', label: 'Pago', value: formatBrl(byStatus('pago')), amount: byStatus('pago') },
      { statusKey: 'aprovado', label: 'Aprovado', value: formatBrl(byStatus('aprovado')), amount: byStatus('aprovado') },
      { statusKey: 'revisado', label: 'Revisado', value: formatBrl(byStatus('revisado')), amount: byStatus('revisado') },
      { statusKey: 'rejeitado', label: 'Rejeitado', value: formatBrl(byStatus('rejeitado')), amount: byStatus('rejeitado') },
      { statusKey: 'negado', label: 'Negado', value: formatBrl(byStatus('negado')), amount: byStatus('negado') },
    ],
  };
}

export function techMatchesExpense(tech, expense) {
  const cod = String(tech?.codtech || '').trim();
  const expCod = String(expense?.codtech || '').trim();
  if (cod && expCod) {
    if (cod === expCod) return true;
    const pad = (s) => (/^\d+$/.test(s) ? s.padStart(4, '0') : s);
    if (pad(cod) === pad(expCod)) return true;
  }
  const name = String(tech?.name || '').trim();
  if (name && String(expense?.techName || '').trim() === name) return true;
  return false;
}

export function expenseMatchesSliceFilters(expense, {
  categories = [],
  statusKeys = [],
  paymentLabels = [],
} = {}) {
  if (categories.length) {
    const cat = String(expense.expenseTypeName || '').trim();
    if (!categories.includes(cat)) return false;
  }
  if (statusKeys.length && !statusKeys.includes(expense.statusKey)) return false;
  if (paymentLabels.length) {
    const pay = formatPaymentType(expense.paymentType);
    if (!paymentLabels.includes(pay)) return false;
  }
  return true;
}
